import React, { useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useT } from "@/lib/LanguageContext";
// Relative on purpose: real modules under Jest, where there is no "@" alias.
import FieldError from "../../components/ui/FieldError";
import { PATHWAYS_ADMIN as IDS } from "../../constants/testIds/pathwaysAdmin";
import { pathwayErrorMessage } from "../../pathways/errors";
import ModalShell from "./ModalShell";

export const ENCOURAGE_MESSAGE_MAX = 2000;

/**
 * "Send encouragement" (spec screen 5, contract §5.1 `POST /encourage`): a pre-written
 * message the practitioner may edit, sent to the stuck learners the dashboard shows. The
 * default text is the locale's (`path.dash.encourageDefault`), as the contract asks; what
 * is sent is whatever the practitioner leaves in the box, trimmed.
 *
 * The server skips anyone no longer enrolled or already finished and says how many it
 * sent and skipped; the caller turns that into its toast.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {{ email: string, name: string }[]} props.recipients
 * @param {(body: { emails: string[], message: string }) => Promise<{ sent: number, skipped: number }>} props.onSend
 * @param {(result: { sent: number, skipped: number }) => void} props.onSent
 * @param {() => void} props.onClose
 */
export default function EncourageModal({ open, recipients, onSend, onSent, onClose }) {
  return (
    <ModalShell open={open} onClose={onClose} labelledBy="pathway-encourage-heading" testId={IDS.encourageModal} panelClassName="max-w-xl">
      {open && <EncourageForm recipients={recipients} onSend={onSend} onSent={onSent} onClose={onClose} />}
    </ModalShell>
  );
}

function EncourageForm({ recipients, onSend, onSent, onClose }) {
  const t = useT("admin");
  const tc = useT("common");
  const [message, setMessage] = useState(() => t("path.dash.encourageDefault"));
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const names = recipients.map((r) => r.name || r.email);

  const send = async () => {
    const text = message.trim();
    if (!text) {
      setError(t("path.dash.encourageEmpty"));
      return;
    }
    setSending(true);
    setError("");
    try {
      const result = await onSend({ emails: recipients.map((r) => r.email), message: text });
      onSent(result);
    } catch (e) {
      setError(pathwayErrorMessage(e, tc, tc("errors.saveFailed")));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="p-5 sm:p-6 space-y-4">
      <h2 id="pathway-encourage-heading" className="font-display text-2xl text-eh-ink">{t("path.dash.encourageTitle")}</h2>
      <p data-testid={IDS.encourageRecipients} className="text-sm text-eh-muted">
        {t(recipients.length === 1 ? "path.dash.encourageToOne" : "path.dash.encourageTo", { n: recipients.length, names: names.join(", ") })}
      </p>
      <div>
        <label htmlFor="pathway-encourage-message" className="block text-sm text-eh-muted mb-1.5">{t("path.dash.encourageMessage")}</label>
        <textarea
          id="pathway-encourage-message"
          value={message}
          onChange={(e) => { setMessage(e.target.value); setError(""); }}
          rows={6}
          maxLength={ENCOURAGE_MESSAGE_MAX}
          data-testid={IDS.encourageMessage}
          className="w-full border border-input rounded-md px-3 py-2 text-sm bg-white resize-y"
        />
        <FieldError message={error} testId={IDS.encourageError} />
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onClose}>{t("path.editor.cancel")}</Button>
        <Button onClick={send} disabled={sending || recipients.length === 0} data-testid={IDS.encourageSend} className="bg-eh-ink text-eh-cream hover:bg-eh-ink/90">
          {sending && <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />}
          {t("path.dash.encourageSend", { n: recipients.length })}
        </Button>
      </div>
    </div>
  );
}
